import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Typography, Spin, Alert, Space } from 'antd';
import { ArrowLeftOutlined, FormOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import DeadChickenModal from '../components/Farm/DeadChickenModal';
import { canRecordDeadChicken } from '../constants/permissions';
import { fetchTodayRecord } from '../utils/deadChicken';

const { Title, Text } = Typography;

const BarnMaintenance = () => {
  const { barnId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [barn, setBarn] = useState(null);
  const [record, setRecord] = useState(null);
  const [deadChickenModalOpen, setDeadChickenModalOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const data = await fetchTodayRecord(barnId);
      setBarn(data.barn);
      setRecord(data.record || null);
    } catch (err) {
      setBarn(null);
      setRecord(null);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [barnId]);

  useEffect(() => {
    if (!canRecordDeadChicken()) {
      navigate('/', { replace: true });
      return;
    }

    loadData();
  }, [barnId, loadData, navigate]);

  const handleDeadChickenSaved = async () => {
    await loadData();
    Swal.fire({
      icon: 'success',
      title: t('barn.deadChicken.saveSuccess'),
      confirmButtonColor: '#8B6914',
      timer: 2000,
      showConfirmButton: false
    });
  };

  if (loading) {
    return (
      <div className="px-3 sm:px-4 py-8 max-w-3xl mx-auto text-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-4 py-4 sm:py-6 max-w-3xl mx-auto space-y-4 sm:space-y-6">
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/')}
        className="!text-wabi-brown-700 !px-0"
      >
        {t('barn.maintenance.back')}
      </Button>

      <Title level={3} className="!mb-0 !text-wabi-brown-800">
        {t('barn.maintenance.title', { barn: barnId })}
      </Title>

      {error && (
        <Alert type="error" message={error} showIcon />
      )}

      {barn && (
        <>
          <Card className="bg-white/90 border-0 shadow-sm">
            <Title level={5} className="!mt-0 !text-wabi-brown-700">
              {t('barn.maintenance.info')}
            </Title>
            <Space direction="vertical" size={4}>
              <Text className="text-gray-600">
                {t('barn.maintenance.capacity')}: {barn.capacity?.toLocaleString()}
              </Text>
              {barn.area != null && (
                <Text className="text-gray-600">
                  {t('barn.maintenance.area')}: {Number(barn.area).toLocaleString()} m²
                </Text>
              )}
            </Space>
          </Card>

          <Card className="bg-white/90 border-0 shadow-sm">
            <Title level={5} className="!mt-0 !text-wabi-brown-700">
              {t('barn.maintenance.actions')}
            </Title>

            <div className="space-y-3">
              <Button
                type="primary"
                size="large"
                icon={<FormOutlined />}
                block
                className="!h-auto !py-3"
                onClick={() => setDeadChickenModalOpen(true)}
              >
                {record
                  ? t('barn.maintenance.editDeadChicken')
                  : t('barn.maintenance.registerDeadChicken')}
              </Button>

              <div className="rounded-lg bg-wabi-cream-50 px-4 py-3">
                <Text className="block text-sm text-gray-500">
                  {t('barn.maintenance.todayDeadChicken')}
                </Text>
                <Text className="block text-2xl font-bold text-wabi-brown-800">
                  {record ? record.employee_count : t('barn.maintenance.noRecord')}
                </Text>
              </div>
            </div>
          </Card>
        </>
      )}

      <DeadChickenModal
        open={deadChickenModalOpen}
        barnId={barnId}
        onClose={() => setDeadChickenModalOpen(false)}
        onSaved={handleDeadChickenSaved}
      />
    </div>
  );
};

export default BarnMaintenance;
